import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '../lib/utils';

export type BadgeVariant =
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'default'
  | 'cancelled'
  | 'trial';

export interface BadgeProps extends HTMLAttributes<HTMLDivElement> {
  variant?: BadgeVariant;
  size?: 'sm' | 'md';
}

const variantStyles: Record<BadgeVariant, string> = {
  success: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800',
  warning: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800',
  error: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800',
  info: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800',
  default: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
  cancelled: 'bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700',
  trial: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800',
};

const statusLabels: Record<string, string> = {
  active: 'Ativo',
  pending: 'Pendente',
  cancelled: 'Cancelado',
  past_due: 'Atrasado',
  trialing: 'Teste',
  paid: 'Pago',
  failed: 'Falhou',
  admin: 'Admin',
  member: 'Membro',
  owner: 'Proprietário',
  accepted: 'Aceito',
  declined: 'Recusado',
};

export const Badge = forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant = 'default', size = 'md', children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border text-xs font-medium',
          variantStyles[variant],
          size === 'sm' && 'text-[10px] px-2 py-0',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Badge.displayName = 'Badge';

export interface StatusBadgeProps extends Omit<BadgeProps, 'variant' | 'children'> {
  status: string;
}

export const StatusBadge = forwardRef<HTMLDivElement, StatusBadgeProps>(
  ({ status, className, size, ...props }, ref) => {
    const variant: BadgeVariant = (() => {
      switch (status.toLowerCase()) {
        case 'active':
        case 'paid':
        case 'accepted':
          return 'success';
        case 'pending':
        case 'past_due':
          return 'warning';
        case 'cancelled':
        case 'failed':
        case 'declined':
          return 'error';
        case 'trialing':
          return 'trial';
        default:
          return 'default';
      }
    })();

    return (
      <Badge ref={ref} variant={variant} size={size} className={className} {...props}>
        {statusLabels[status] || status}
      </Badge>
    );
  }
);

StatusBadge.displayName = 'StatusBadge';
